import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { G6_ECONOMIES } from '../design-system';
import { authAPI } from '../services/api';
import { useAuth } from './AuthContext';

const STORAGE_FOCUS = 'oikra:focus_country_iso';
const STORAGE_VIEW = 'oikra:panorama_view';

const ScopeContext = createContext(null);

function readStored(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v || fallback;
  } catch {
    return fallback;
  }
}

export function ScopeProvider({ children }) {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const hydratedFromUser = useRef(false);

  const [focusCountryIso, setFocusCountryIsoState] = useState(() =>
    readStored(STORAGE_FOCUS, 'AR')
  );
  const [panoramaView, setPanoramaViewState] = useState(() =>
    readStored(STORAGE_VIEW, 'global')
  );

  useEffect(() => {
    if (!user || hydratedFromUser.current) return;
    if (user.focus_country_iso) {
      const iso = String(user.focus_country_iso).toUpperCase();
      setFocusCountryIsoState(iso);
      try {
        localStorage.setItem(STORAGE_FOCUS, iso);
      } catch {
        /* ignore */
      }
    }
    if (user.panorama_view === 'country' || user.panorama_view === 'global') {
      setPanoramaViewState(user.panorama_view);
      try {
        localStorage.setItem(STORAGE_VIEW, user.panorama_view);
      } catch {
        /* ignore */
      }
    }
    hydratedFromUser.current = true;
  }, [user]);

  const persistScope = useCallback(
    async (patch) => {
      if (!isAuthenticated) return { ok: true, skipped: true };
      try {
        await authAPI.updateProfile(patch);
        if (refreshUser) await refreshUser();
        return { ok: true };
      } catch {
        return { ok: false };
      }
    },
    [isAuthenticated, refreshUser]
  );

  const setFocusCountryIso = useCallback(
    async (iso) => {
      const next = String(iso || 'AR').toUpperCase();
      setFocusCountryIsoState(next);
      try {
        localStorage.setItem(STORAGE_FOCUS, next);
      } catch {
        /* ignore */
      }
      return persistScope({ focus_country_iso: next });
    },
    [persistScope]
  );

  const setPanoramaView = useCallback(
    async (view) => {
      const next = view === 'country' ? 'country' : 'global';
      setPanoramaViewState(next);
      try {
        localStorage.setItem(STORAGE_VIEW, next);
      } catch {
        /* ignore */
      }
      return persistScope({ panorama_view: next });
    },
    [persistScope]
  );

  const focusEconomy = useMemo(
    () => G6_ECONOMIES.find((e) => e.iso === focusCountryIso) || G6_ECONOMIES.find((e) => e.iso === 'AR'),
    [focusCountryIso]
  );

  const value = useMemo(
    () => ({
      focusCountryIso,
      setFocusCountryIso,
      focusEconomy,
      panoramaView,
      setPanoramaView,
      economies: G6_ECONOMIES,
    }),
    [focusCountryIso, setFocusCountryIso, focusEconomy, panoramaView, setPanoramaView]
  );

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope() {
  const ctx = useContext(ScopeContext);
  if (!ctx) {
    throw new Error('useScope must be used within ScopeProvider');
  }
  return ctx;
}

/** Like useScope, but undefined outside the provider: for widgets that follow the focus country when there is one. */
export function useOptionalScope() {
  return useContext(ScopeContext);
}
